/**
 * @file        src/pages/communicationLog/CommunicationLog.jsx
 * @description Account Management → Communication Log. Read-only, paginated
 *              list of every outbound email attempt captured by either
 *              backend (GET /api/communication-logs) — customer-facing sends
 *              from booking_backend (booking confirmations, OTPs, hotel
 *              vouchers…) and admin-initiated sends from admin_backend
 *              (password reset, share-by-email). `brand: 'default'`/`null`
 *              means a tenant-wide/admin-staff send not tied to a storefront
 *              — shown as "Default / Admin". Mirrors the Transaction Log /
 *              Supplier API Logs list-page shape (search-row filter bar +
 *              paginated table), just with no row mutations.
 *
 *              A CHANNEL column sits beside the send: `communication_logs.channel`
 *              has existed since migration 040 and the API has always returned it,
 *              but nothing showed it. Only 'email' is written today — SMS and
 *              WhatsApp are the next two, and the column is ready for them.
 *
 *              "Details" opens the whole row. It needs no fetch: the list endpoint
 *              already returns every column, so the row in hand IS the record — and
 *              the table has to clip the subject, the cc list and the failure
 *              reason, which is exactly what someone opening a failed row wants.
 * @author      Kinetika Labs
 * @created     2026-08-05
 * @changelog
 *   - 2026-09-22 Claude — Channel column (Email / SMS / WhatsApp) + a Details
 *                          modal showing every logged field untruncated.
 *   - 2026-09-22 Claude — Details also renders the MESSAGE BODY (migration 066),
 *                          fetched per row from GET /communication-logs/:id. The
 *                          HTML is rendered inside a sandboxed iframe, never into
 *                          this document: these bodies interpolate customer-typed
 *                          text (a contact enquiry is the plain case), so putting
 *                          one in the console's own DOM would make a template that
 *                          ever forgot to escape into stored XSS against the admin
 *                          reading the log.
 *   - 2026-08-05 Claude — Initial.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';
import { listCommunicationLogs, getCommunicationLog } from '../../api/communicationLogs';
import { formatDateTime } from '../../lib/format';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'not_configured', label: 'Not configured' },
];

const STATUS_TONE = { sent: 'ok', failed: 'rej', not_configured: 'warn' };
const STATUS_LABEL = { sent: 'Sent', failed: 'Failed', not_configured: 'Not configured' };

const SENT_BY_LABEL = { booking_backend: 'Customer site', admin_backend: 'Admin console' };

// Only 'email' is written today — both mailers hard-code it — but the column has
// been in the schema since migration 040 and SMS / WhatsApp are the next two.
const CHANNEL_LABEL = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };

// Mirrors CREDENTIAL_TEMPLATES in admin_backend's communicationLogs.repo.js and
// booking_backend's communicationLogService.js — used ONLY to explain the masking
// in the detail view. The masking itself happens server-side, at write time.
const CREDENTIAL_TEMPLATES = new Set(['otpEmail', 'forgotPasswordOtpEmail', 'adminPasswordResetOtp']);

// An unrecognised channel is shown AS IT WAS STORED rather than falling back to
// "Email": a new channel must be visible the day it starts writing, not disguised
// as the old one.
const channelLabel = (c) => CHANNEL_LABEL[String(c || '').toLowerCase()] || c || '—';

function Field({ label, children }) {
  return (
    <div><span className="muted">{label}</span><div>{children || <span className="muted">—</span>}</div></div>
  );
}

/**
 * The stored message, in a SANDBOXED iframe — `sandbox=""` grants nothing back:
 * no scripts, no forms, no same-origin access to the console. These bodies carry
 * customer-typed text (a contact enquiry most obviously), so rendering one into
 * this document would turn any template that ever forgot to escape into stored
 * XSS against the admin who opens the log. The `no-referrer` meta keeps a remote
 * image in an email from telling its host which admin page pulled it.
 */
function BodyPreview({ html }) {
  return (
    <iframe
      title="Message body"
      sandbox=""
      srcDoc={`<meta name="referrer" content="no-referrer">${html}`}
      style={{ width: '100%', height: 420, border: '1px solid var(--stroke)', borderRadius: 10, background: '#fff' }}
    />
  );
}

const preStyle = {
  margin: 0, maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere', fontSize: '.75rem', lineHeight: 1.5,
  border: '1px solid var(--stroke)', borderRadius: 10, padding: 12, background: 'var(--field)',
};

/**
 * One send, in full. The metadata comes from the list row already in hand, so the
 * modal paints instantly; only the BODY needs a fetch, because the list endpoint
 * deliberately does not carry it (25 rendered e-tickets would be megabytes).
 */
function LogDetailModal({ row, brandLabel, onClose }) {
  const [showSource, setShowSource] = useState(false);

  const { data: detail, isLoading: bodyLoading, isError: bodyError } = useQuery({
    queryKey: qk.communicationLogs.detail(row?.id),
    queryFn: () => getCommunicationLog(row.id),
    enabled: Boolean(row?.id),
  });

  // Hooks must run on every render, so the empty guard comes after them.
  if (!row) return null;
  const bodyHtml = detail?.data?.body_html || '';
  const bodyText = detail?.data?.body_text || '';
  const hasBody = Boolean(bodyHtml || bodyText);
  const related = row.related_type
    ? <>{row.related_type}{row.related_reference ? <> · <span className="mono">{row.related_reference}</span></> : null}</>
    : null;

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass">
        <div className="lk-modal-head">
          <h3>{channelLabel(row.channel)} · #{row.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          <div className="det-grid">
            <Field label="Sent">{formatDateTime(row.created_at)}</Field>
            <Field label="Channel">{channelLabel(row.channel)}</Field>
            <Field label="Status">
              <Chip tone={STATUS_TONE[row.status] || 'pend'}>{STATUS_LABEL[row.status] || row.status}</Chip>
            </Field>
            <Field label="Brand">{brandLabel(row.brand)}</Field>
            <Field label="Template"><span className="mono">{row.template}</span></Field>
            <Field label="Sent By">{SENT_BY_LABEL[row.sent_by] || row.sent_by}</Field>
          </div>

          <h4 className="det-h">Recipients</h4>
          <div className="det-grid">
            <Field label="To">{row.to_email}</Field>
            <Field label="CC">{row.cc}</Field>
          </div>

          <h4 className="det-h">Message</h4>
          <div className="det-grid">
            <Field label="Subject">{row.subject}</Field>
            <Field label="Related">{related}</Field>
          </div>

          {row.status !== 'sent' && row.error ? (
            <>
              <h4 className="det-h">Failure</h4>
              {/* The table can only show the first line of this, and it is the
                  whole reason for opening a failed row. */}
              <p className="field-error" style={{ overflowWrap: 'anywhere' }}>{row.error}</p>
            </>
          ) : null}

          <h4 className="det-h" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span>Message body</span>
            {bodyHtml ? (
              <button type="button" className="btn xs ghost" onClick={() => setShowSource((v) => !v)}>
                {showSource ? 'Preview' : 'View source'}
              </button>
            ) : null}
          </h4>

          {bodyLoading ? <p className="muted">Loading the message…</p> : null}
          {bodyError ? <p className="field-error">Could not load the message body.</p> : null}

          {!bodyLoading && !bodyError && !hasBody ? (
            // Two different causes, one NULL: nulled by the retention job, or sent
            // before bodies were kept at all. Neither is an error, and claiming
            // either one specifically would be a guess.
            <p className="muted" style={{ fontSize: '.8rem' }}>
              No body stored — this was sent before message bodies were retained, or it has
              passed the retention window. The log entry itself is kept permanently.
            </p>
          ) : null}

          {!bodyLoading && bodyHtml && !showSource ? <BodyPreview html={bodyHtml} /> : null}
          {!bodyLoading && bodyHtml && showSource ? <pre className="mono" style={preStyle}>{bodyHtml}</pre> : null}
          {!bodyLoading && !bodyHtml && bodyText ? <pre style={preStyle}>{bodyText}</pre> : null}

          {/* A one-time login code is a live credential for as long as it is valid,
              and this page is readable by every Account Management admin — so the
              backend masks it before storing. Say so, or a row of asterisks reads
              as a broken template. */}
          {CREDENTIAL_TEMPLATES.has(row.template) && hasBody ? (
            <p className="muted" style={{ fontSize: '.75rem' }}>
              The one-time code in this message is masked — it is a live credential and is
              deliberately not retained.
            </p>
          ) : null}

          <div className="form-actions">
            <button className="btn ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CommunicationLog() {
  const { options: brandOptions, isLoading: brandsLoading, labelOf } = useBrands();

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [brand, setBrand] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detailRow, setDetailRow] = useState(null);

  const brandFilterOptions = [
    { value: '', label: 'All brands' },
    { value: 'default', label: 'Default / Admin' },
    ...brandOptions,
  ];

  const brandLabel = (b) => {
    if (b === null || b === undefined || b === 'default') return 'Default / Admin';
    return labelOf(b);
  };

  const listParams = {
    page,
    pageSize,
    ...(brand ? { brand } : {}),
    ...(status ? { status } : {}),
    ...(appliedSearch ? { search: appliedSearch } : {}),
    ...(from ? { from } : {}),
    // Inclusive end-of-day — a bare date string compares as 00:00:00 and would
    // silently exclude every row sent on "to" itself.
    ...(to ? { to: `${to} 23:59:59.999` } : {}),
  };

  const { data: list, isLoading } = useQuery({
    queryKey: qk.communicationLogs.list(listParams),
    queryFn: () => listCommunicationLogs(listParams),
    placeholderData: keepPreviousData,
  });

  const body = list?.data || {};
  const rows = body.rows || [];
  const total = body.total || 0;

  function onSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  }

  return (
    <>
      <PageHead
        title="Communication Log"
        subtitle="Account Management · every outbound message attempt, across both backends"
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={onSearchSubmit}>
          <div className="field">
            <label>To / Related Ref / Subject</label>
            <input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Brand</label>
            <Select
              value={brand}
              onChange={(v) => { setBrand(v); setPage(1); }}
              options={brandFilterOptions}
              placeholder={brandsLoading ? 'Loading…' : 'All brands'}
            />
          </div>
          <div className="field">
            <label>Status</label>
            <Select
              value={status}
              onChange={(v) => { setStatus(v); setPage(1); }}
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Sent</th><th>Channel</th><th>Brand</th><th>To</th><th>Subject</th>
                  <th>Template</th><th>Status</th><th>Related</th><th>Sent By</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={10} className="empty">No communication log entries for these filters.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">{formatDateTime(r.created_at)}</td>
                    <td>{channelLabel(r.channel)}</td>
                    <td>{brandLabel(r.brand)}</td>
                    <td>
                      <div>{r.to_email}</div>
                      {r.cc ? <small className="muted">cc: {r.cc}</small> : null}
                    </td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.subject || ''}>
                      {r.subject || <span className="muted">—</span>}
                    </td>
                    <td className="mono">{r.template || <span className="muted">—</span>}</td>
                    <td>
                      <Chip tone={STATUS_TONE[r.status] || 'pend'}>{STATUS_LABEL[r.status] || r.status}</Chip>
                      {r.status !== 'sent' && r.error ? (
                        <div>
                          <small
                            className="field-error"
                            style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.error}
                          >
                            {r.error}
                          </small>
                        </div>
                      ) : null}
                    </td>
                    <td className="muted">
                      {r.related_type
                        ? <>{r.related_type}{r.related_reference ? <> · <span className="mono">{r.related_reference}</span></> : null}</>
                        : '—'}
                    </td>
                    <td className="muted">{SENT_BY_LABEL[r.sent_by] || r.sent_by || '—'}</td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn xs ghost" title="Details" onClick={() => setDetailRow(r)}>
                          <Icon name="search" /> Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      <LogDetailModal row={detailRow} brandLabel={brandLabel} onClose={() => setDetailRow(null)} />
    </>
  );
}
